/**
 * 평문(txt) 편집 도구 — 호스트 에이전트 루프가 실행한다 (2026-08-15, Phase B).
 *
 * ## 왜 글자 오프셋이 아니라 줄·문자열인가
 *
 * 모델은 문자 인덱스를 못 센다. 한글이 섞이면 더 못 센다(코드 유닛·바이트·자소가 다 다르다).
 * 오프셋을 인자로 받으면 "조금 어긋난 자리를 덮어쓰는" 실패가 나오는데, 그건 사용자가
 * 알아채기 가장 어려운 종류의 손상이다.
 *
 * 그래서 두 축만 준다 — **줄 번호**(읽으면 그대로 보인다)와 **정확한 문자열**(모델이 방금
 * 읽은 것을 그대로 되돌려 쓴다). 둘 다 틀리면 **찾지 못했다고 오류를 돌려주고 아무것도
 * 바꾸지 않는다.** 루프가 그 문구를 모델에게 되먹여 고쳐 부르게 한다.
 *
 * ## 실패는 예외가 아니라 글이다
 *
 * 도구가 던지면 루프가 흡수하지만(대화는 안 끊긴다) 모델은 왜 실패했는지 모른다.
 * 여기서는 항상 `isError` + 사람이 읽을 수 있는 사유를 돌려준다 — 모델이 다음 수를 고를 수 있게.
 */

import type { HostAgentTool, HostToolResult } from '../../../api/office-ai-agent';
import { resolveRenameName } from './rename-name';

/** 이 도구들이 문서에 닿는 창구. 편집기 핸들에서 필요한 것만 좁혀 받는다. */
export interface TextToolTarget {
  getText: () => string | null;
  /**
   * 있으면 **고치는 도구 셋**(치환·줄 교체·줄 삽입)이 생긴다. 없으면 읽기만 남는다
   * (한글은 아직 여기 — 본문 쓰기 창구가 없다. 계획서 Phase C).
   *
   * ★ 없는 도구를 목록에 넣지 않는 것이 규칙이다 — 넣어 두고 실행할 때 거절하면 모델이
   *   "고쳐 드릴까요?" 하고는 다음 턴에 "못 합니다" 라고 한다.
   */
  setText?: (text: string) => boolean;
  /** 지금 파일 이름(확장자 포함). `renameFile` 이 있을 때만 쓴다. */
  getFileName?: () => string;
  /** 있으면 `rename_file` 도구가 생긴다. 적용된 이름을 돌려준다. */
  renameFile?: (name: string) => Promise<string>;
}

/** `read_text` 가 한 번에 돌려주는 글자 상한 — 서버 예산(110KB)을 혼자 먹지 않게. */
const READ_MAX_CHARS = 12_000;

const NOT_LOADED = '문서를 아직 읽지 못했습니다. 잠시 후 다시 시도하세요.';

export function createTextTools(target: TextToolTarget): HostAgentTool[] {
  const read = (): string | null => target.getText();
  const canWrite = !!target.setText;
  const rename = target.renameFile;
  const currentName = target.getFileName;

  /**
   * 쓰기 공통 — 못 읽었거나 편집기가 거절하면 같은 모양의 오류로 돌려준다.
   *
   * ⚠ `target.setText(...)` 로 **객체를 통해** 부른다. 함수만 떼어 두면 `this` 를 쓰는 창구
   *   구현(테스트 더블이 그렇다)이 조용히 깨진다.
   */
  const write = (next: string, summary: string): HostToolResult => {
    if (!target.setText?.(next)) return { output: '편집기가 변경을 받지 못했습니다.', isError: true };
    return { output: `적용했습니다. ${summary}`, summary };
  };

  const tools: HostAgentTool[] = [
    {
      name: 'read_text',
      description:
        'Read the document with line numbers. Always read before editing so line numbers and exact text are current. ' +
        'Omit both arguments to read the whole document.',
      inputSchema: {
        type: 'object',
        properties: {
          fromLine: { type: 'integer', minimum: 1, description: '1-based first line (inclusive)' },
          toLine: { type: 'integer', minimum: 1, description: '1-based last line (inclusive)' },
        },
      },
      execute: (input) => {
        const text = read();
        if (text === null) return { output: NOT_LOADED, isError: true };
        const lines = text.split('\n');
        const from = clampLine(asInt(input.fromLine, 1), lines.length);
        const to = clampLine(asInt(input.toLine, lines.length), lines.length);
        if (from > to) return { output: `fromLine(${from}) 이 toLine(${to}) 보다 큽니다.`, isError: true };

        const body = lines
          .slice(from - 1, to)
          .map((line, i) => `${from + i}: ${line}`)
          .join('\n');
        const clipped = body.length > READ_MAX_CHARS ? `${body.slice(0, READ_MAX_CHARS)}\n…(생략됨)` : body;
        return {
          output: `총 ${lines.length}줄 중 ${from}–${to}\n${clipped}`,
          summary: `${from}–${to}줄 읽음`,
        };
      },
    },

    {
      name: 'replace_text',
      description:
        'Replace an exact substring. `find` must match the document character for character (copy it from read_text output, without the line-number prefix). ' +
        'Fails without changing anything when `find` is absent, or when it occurs more than once and `all` is not set.',
      inputSchema: {
        type: 'object',
        properties: {
          find: { type: 'string', minLength: 1, description: 'exact text to find' },
          replace: { type: 'string', description: 'text to put in its place (empty string deletes)' },
          all: { type: 'boolean', description: 'replace every occurrence (default false)' },
        },
        required: ['find', 'replace'],
      },
      execute: (input) => {
        const text = read();
        if (text === null) return { output: NOT_LOADED, isError: true };
        const find = asString(input.find);
        const replace = asString(input.replace);
        if (!find) return { output: 'find 가 비었습니다.', isError: true };

        const hits = countOccurrences(text, find);
        if (hits === 0) {
          return {
            output:
              `"${preview(find)}" 를 찾지 못했습니다. read_text 로 현재 내용을 다시 읽고, ` +
              '줄 번호 접두("12: ")를 뺀 본문만 그대로 넣으세요.',
            isError: true,
          };
        }
        if (hits > 1 && input.all !== true) {
          return {
            output: `"${preview(find)}" 가 ${hits}곳에 있습니다. 더 긴 문맥을 넣어 하나만 가리키거나 all:true 로 부르세요.`,
            isError: true,
          };
        }
        const next = input.all === true ? text.split(find).join(replace) : text.replace(find, replace);
        return write(next, `${hits > 1 ? `${hits}곳` : '1곳'} 치환`);
      },
    },

    {
      name: 'replace_lines',
      description:
        'Replace lines [fromLine, toLine] (1-based, inclusive) with `text`. ' +
        'Use this to rewrite a block, or the whole document by passing the full line range that read_text reported.',
      inputSchema: {
        type: 'object',
        properties: {
          fromLine: { type: 'integer', minimum: 1 },
          toLine: { type: 'integer', minimum: 1 },
          text: { type: 'string', description: 'replacement block; may contain newlines. Empty string deletes the lines.' },
        },
        required: ['fromLine', 'toLine', 'text'],
      },
      execute: (input) => {
        const text = read();
        if (text === null) return { output: NOT_LOADED, isError: true };
        const lines = text.split('\n');
        const from = asInt(input.fromLine, 0);
        const to = asInt(input.toLine, 0);
        if (from < 1 || to < 1) return { output: 'fromLine·toLine 은 1 이상이어야 합니다.', isError: true };
        if (from > lines.length) {
          return { output: `문서는 ${lines.length}줄인데 fromLine 이 ${from} 입니다.`, isError: true };
        }
        if (from > to) return { output: `fromLine(${from}) 이 toLine(${to}) 보다 큽니다.`, isError: true };

        const end = Math.min(to, lines.length);
        const body = asString(input.text);
        const next = [...lines.slice(0, from - 1), ...(body === '' ? [] : body.split('\n')), ...lines.slice(end)].join('\n');
        return write(next, `${from}–${end}줄 교체`);
      },
    },

    {
      name: 'insert_lines',
      description:
        'Insert `text` as new lines after `afterLine` (1-based). Pass afterLine: 0 to insert at the very top. Nothing is overwritten.',
      inputSchema: {
        type: 'object',
        properties: {
          afterLine: { type: 'integer', minimum: 0 },
          text: { type: 'string', minLength: 1 },
        },
        required: ['afterLine', 'text'],
      },
      execute: (input) => {
        const text = read();
        if (text === null) return { output: NOT_LOADED, isError: true };
        const lines = text.split('\n');
        const after = asInt(input.afterLine, -1);
        if (after < 0) return { output: 'afterLine 은 0 이상이어야 합니다(0 = 맨 위).', isError: true };
        if (after > lines.length) {
          return { output: `문서는 ${lines.length}줄인데 afterLine 이 ${after} 입니다.`, isError: true };
        }
        const body = asString(input.text);
        if (!body) return { output: 'text 가 비었습니다.', isError: true };

        const next = [...lines.slice(0, after), ...body.split('\n'), ...lines.slice(after)].join('\n');
        return write(next, `${after}줄 뒤에 ${body.split('\n').length}줄 삽입`);
      },
    },

    // ── 파일 이름. 창구가 있을 때만 만든다 — 없는 도구를 목록에 넣으면 모델이
    //    "해 드릴까요?" 하고는 못 하는, 사용자가 겪은 그 앞뒤 안 맞는 대화가 된다.
    ...(rename && currentName ? [createRenameTool(rename, currentName)] : []),
  ];

  // 쓰기 창구가 없는 편집기(한글)에서는 **고치는 도구를 통째로 뺀다** — 위 `setText` 주석의
  // 같은 이유다. 읽기와 파일 이름은 남으므로 카드는 도구 모드로 계속 돈다.
  return canWrite ? tools : tools.filter((t) => !WRITE_TOOL_NAMES.has(t.name));
}

/** `setText` 가 있어야 성립하는 도구들. */
const WRITE_TOOL_NAMES = new Set(['replace_text', 'replace_lines', 'insert_lines']);

/**
 * 파일 이름 바꾸기.
 *
 * 판정 규칙은 `rename-name.ts` 에 있다 — GenOffice 다섯이 지나는 호스트 창구와 **같은
 * 규칙**이어야 하기 때문이다(편집기에 따라 되고 안 되면 규칙으로 읽히지 않는다).
 */
export function createRenameTool(rename: (name: string) => Promise<string>, currentName: () => string): HostAgentTool {
  return {
    name: 'rename_file',
    description:
      'Rename the open file. Give the new name only — no folders or paths. ' +
      'The extension is preserved: omit it, or repeat the current one. Changing the extension is rejected.',
    inputSchema: {
      type: 'object',
      properties: { name: { type: 'string', minLength: 1, description: 'new file name' } },
      required: ['name'],
    },
    execute: async (input) => {
      const now = currentName();
      const checked = resolveRenameName(now, asString(input.name));
      if (!checked.ok) return { output: checked.reason, isError: true };
      const next = checked.name;

      if (next === now) return { output: `이름이 이미 "${now}" 입니다. 바꾸지 않았습니다.`, summary: '이름 그대로' };

      try {
        const applied = await rename(next);
        return { output: `파일 이름을 "${applied}" 로 바꿨습니다.`, summary: `이름 → ${applied}` };
      } catch (err) {
        // 서버가 거절하는 이유가 여럿이다(중복 이름·권한·길이). 사유를 그대로 넘겨
        // 모델이 다른 이름을 고르거나 사용자에게 설명하게 한다.
        return { output: `이름을 바꾸지 못했습니다: ${(err as Error)?.message ?? '알 수 없는 오류'}`, isError: true };
      }
    },
  };
}

function asInt(v: unknown, fallback: number): number {
  if (typeof v === 'number' && Number.isFinite(v)) return Math.trunc(v);
  // 모델이 숫자를 문자열로 싣는 일이 잦다 — 되는 것은 받아 준다.
  if (typeof v === 'string' && /^-?\d+$/.test(v.trim())) return Number.parseInt(v, 10);
  return fallback;
}

function asString(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

function clampLine(n: number, total: number): number {
  return Math.max(1, Math.min(n, Math.max(total, 1)));
}

function countOccurrences(haystack: string, needle: string): number {
  let count = 0;
  let from = 0;
  for (;;) {
    const at = haystack.indexOf(needle, from);
    if (at < 0) return count;
    count += 1;
    from = at + needle.length;
  }
}

function preview(s: string): string {
  const one = s.replace(/\n/g, '⏎');
  return one.length > 40 ? `${one.slice(0, 40)}…` : one;
}
